import { LuxThemeProvider } from '@gemini/fe-ds-core';
import { LCObTokens } from '@static/LC/LC.obTokens';
import { obTokensValues } from '@static/obTokensValues';
import { InfoUser, useOnlineBookingStore } from '@store/onlineBookingStore';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrandNameList } from '@utils/consts/brandNameList';
import { useCallback, useEffect, useMemo } from 'react';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { Modes } from '@utils/enums/Modes.enum';
import { UrlsProvider } from '@utils/contexts/UrlsContext';
import App from '../App';
import { resetApp } from '@utils/functions';
import Environment from '../Environment';
import { TOObTokens } from '@static/TO/TO.obTokens';
import sofiaProRegular from '@assets/fonts/Sofia-Pro-Regular.woff';
import sofiaProSemiBold from '@assets/fonts/Sofia-Pro-Semi-Bold.woff';
import sofiaProBold from '@assets/fonts/Sofia-Pro-Bold.woff';
import minervaModernRegular from '@assets/fonts/Minerva-Modern-Regular.woff';
import minervaModernBold from '@assets/fonts/Minerva-Modern-Bold.woff';
import { generateTheme, LuxPaletteTypes, palette } from '@gemini/fe-ds-themes';
import helveticaForTarget from '@assets/fonts/Helvetica-For-Target.otf';
import helveticaForTargetBold from '@assets/fonts/Helvetica-For-Target-Bold.woff';
import { FlowModes } from '@utils/enums/FlowModes.enum';
import { IntegrationModes } from '@utils/enums/IntegrationModes.enum';
import { Links } from 'src/embedded-app/EmbeddedApp';

interface CommonConfigurationProps {
  basePath: string;
  mode: Modes;
  practiceId?: string;
  providerId?: string;
  appointmentType?: string;
  brandName?: string;
  jwtToken?: string;
  infoUser?: InfoUser;
  hideHeader?: boolean;
  environmentConfig?: Record<string, string>;
  collapsableMobileBookingDetails?: boolean;
  appointmentId?: string;
  flowMode?: FlowModes;
  integrationMode?: IntegrationModes;
  rescheduleUrl?: string;
  cancelUrl?: string;
  pageCountry?: string;
  pageLanguage?: string;
  links?: Links;
  hideBackToBrandHomePage?: boolean;
  refid?: string;
}

const queryClient = new QueryClient();

/**
 * CommonConfiguration is the main component that wraps the application and provides the necessary context for the application to work.
 * It is used in both standalone and embedded modes.
 * @param basePath - The base path of the application.
 * @param mode - The mode of the application.
 * @param practiceId - The practice id.
 * @param providerId - The provider id.
 * @param appointmentType - The appointment type.
 * @param brandName - The brand name.
 * @param jwtToken - The jwt token.
 * @param hideHeader - Whether to hide the header.
 * @param collapsableMobileBookingDetails - Whether to enable collapsable mobile booking details.
 * @param environmentConfig - The environment configuration.
 * @param infoUser - The user information.
 * @param appointmentId - The appointment id.
 * @param flowMode - The flow mode (default, reschedule, cancel, confirm).
 * @param integrationMode - The integration mode (standalone, wcs).
 * @param rescheduleUrl - The URL for rescheduling flow.
 * @param cancelUrl - The URL for canceling flow.
 * @param pageCountry - The country code.
 * @param pageLanguage - The language code.
 * @param links - Custom links for the application (e.g., terms and conditions, privacy policy).
 * @param hideBackToBrandHomePage - Boolean to hide back to LC/TO home page CTA in cancellation flow.
 * @param refid - optional refid for GL purposes.
 */
function CommonConfiguration({
  basePath,
  mode,
  practiceId,
  providerId,
  appointmentType,
  brandName,
  jwtToken,
  infoUser,
  integrationMode = IntegrationModes.STANDALONE,
  hideHeader = false,
  collapsableMobileBookingDetails = false,
  environmentConfig,
  appointmentId,
  rescheduleUrl,
  cancelUrl,
  flowMode = FlowModes.DEFAULT,
  pageCountry,
  pageLanguage,
  links,
  hideBackToBrandHomePage = false,
  refid,
}: CommonConfigurationProps) {
  const colorConfig = useOnlineBookingStore(
    state => state.generalConfig?.customization,
  );
  const currentPracticeId = useOnlineBookingStore(state => state.practiceId);
  const setRefid = useOnlineBookingStore(state => state.setRefid);
  const setPracticeId = useOnlineBookingStore(state => state.setPracticeId);
  const setAppointmentId = useOnlineBookingStore(
    state => state.setAppointmentId,
  );
  const setProviderIdFromUrl = useOnlineBookingStore(
    state => state.setProviderIdFromUrl,
  );
  const setAppTypeIdFromUrl = useOnlineBookingStore(
    state => state.setAppTypeIdFromUrl,
  );
  const setFlowMode = useOnlineBookingStore(state => state.setFlowMode);
  const setIntegrationMode = useOnlineBookingStore(
    state => state.setIntegrationMode,
  );
  const setBrandName = useOnlineBookingStore(state => state.setBrandName);
  const setJwtToken = useOnlineBookingStore(state => state.setJwtToken);
  const setInfoUser = useOnlineBookingStore(state => state.setInfoUser);

  const setRescheduleUrl = useOnlineBookingStore(
    state => state.setRescheduleUrl,
  );
  const setCancelUrl = useOnlineBookingStore(state => state.setCancelUrl);
  const brandNameFromStore = useOnlineBookingStore(state => state.brandName);

  const setPageCountry = useOnlineBookingStore(state => state.setPageCountry);
  const setPageLanguage = useOnlineBookingStore(state => state.setPageLanguage);
  const setLinks = useOnlineBookingStore(state => state.setLinks);
  const setHideBackToBrandHomePage = useOnlineBookingStore(
    state => state.setHideBackToBrandHomePage,
  );

  useEffect(() => {
    /**
     * If the practice id is provided, we need to reset the app and set the practice id, brand name, provider id, appointment type and jwt token.
     * We expect all the parameters to be provided at the same time.
     */
    if (practiceId) {
      if (practiceId !== currentPracticeId) {
        resetApp();
      }
      setFlowMode(flowMode);
      setIntegrationMode(integrationMode);
      setPracticeId(practiceId);
      setAppointmentId(appointmentId);
      setBrandName(brandName);
      setProviderIdFromUrl(providerId);
      setAppTypeIdFromUrl(appointmentType);
      setJwtToken(jwtToken);
      setInfoUser(infoUser);
      setRescheduleUrl(rescheduleUrl);
      setCancelUrl(cancelUrl);
      setPageCountry(pageCountry);
      setPageLanguage(pageLanguage);
      setLinks(links);
      setHideBackToBrandHomePage(hideBackToBrandHomePage);
      setRefid(refid);
    }
  }, [
    currentPracticeId,
    practiceId,
    brandName,
    providerId,
    appointmentType,
    jwtToken,
    infoUser,
    rescheduleUrl,
    cancelUrl,
    pageCountry,
    pageLanguage,
    hideBackToBrandHomePage,
    refid,
    setFlowMode,
    setPracticeId,
    setAppointmentId,
    setBrandName,
    setProviderIdFromUrl,
    setAppTypeIdFromUrl,
    setJwtToken,
    setInfoUser,
    setRescheduleUrl,
    setCancelUrl,
    setPageCountry,
    setPageLanguage,
    setLinks,
    setHideBackToBrandHomePage,
    setRefid,
  ]);

  /**
   * Returns the tokens for the current brand.
   */
  const tokens = useCallback(() => {
    console.log('LCObTokens', brandNameFromStore);
    switch (brandNameFromStore) {
      case BrandNameList.LC:
        return LCObTokens;
      case BrandNameList.TO:
        return TOObTokens;
      default:
        return obTokensValues(colorConfig);
    }
  }, [brandNameFromStore, colorConfig]);

  const theme = useMemo(() => {
    const obTokens: any = tokens();
    const currentPalette: LuxPaletteTypes = {
      ...palette.LUX,
      obTokens,
      applicationName: 'OnlineBooking',
    };
    const currentTheme = generateTheme(currentPalette);
    /**
     * Same breakpoints for all brands including OB-VX
     */
    currentTheme.breakpoints.values = {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 1024,
      xl: 1440,
    };

    /**
     * Custom typography for brandName for TAB enhancement
     */
    if (brandNameFromStore) {
      /**
       * The typography is the same for all brands, but the font family is different.
       * We need to override the font family for the current brand.
       */
      currentTheme.typography = {
        ...currentTheme.typography,
        textMedium: {
          fontFamily: obTokens.textBaseFamily,
          fontSize: obTokens.textSmSize,
          fontWeight: '700',
          lineHeight: obTokens.textSmLineHeight,
          letterSpacing: obTokens.textSmKerning,
        },
        textMdRegular: {
          fontFamily: obTokens.textBaseFamily,
          fontSize: obTokens.textSmSize,
          fontWeight: '400',
          lineHeight: obTokens.textSmLineHeight,
          letterSpacing: obTokens.textSmKerning,
        },
        heading4: {
          fontFamily: obTokens.textH4Family,
          fontSize: obTokens.textH4DeskSize,
          fontWeight: '700',
          lineHeight: obTokens.textH4DeskLineHeight,
          letterSpacing: obTokens.textH4DeskKerning,
          [currentTheme.breakpoints.down('sm')]: {
            fontSize: obTokens.textH4MobSize,
            lineHeight: obTokens.textH4MobLineHeight,
            letterSpacing: obTokens.textH4MobKerning,
          },
        },
        heading3: {
          fontFamily: obTokens.textH3Family,
          fontSize: obTokens.textH3DeskSize,
          fontWeight: brandName === BrandNameList.LC ? '400' : '700',
          lineHeight: obTokens.textH3DeskLineHeight,
          letterSpacing: obTokens.textH3DeskKerning,
          [currentTheme.breakpoints.down('sm')]: {
            fontSize: obTokens.textH3MobSize,
            lineHeight: obTokens.textH3MobLineHeight,
            letterSpacing: obTokens.textH3MobKerning,
          },
        },
        heading2: {
          fontFamily: obTokens.textH2Family,
          fontSize: obTokens.textH2DeskSize,
          fontWeight: '700',
          lineHeight: obTokens.textH2DeskLineHeight,
          letterSpacing: obTokens.textH2DeskKerning,
          [currentTheme.breakpoints.down('sm')]: {
            fontSize: obTokens.textH2MobSize,
            lineHeight: obTokens.textH2MobLineHeight,
            letterSpacing: obTokens.textH2MobKerning,
          },
        },
        heading1: {
          fontFamily: obTokens.textH1Family,
          fontSize: obTokens.textH1DeskSize,
          fontWeight: '700',
          lineHeight: obTokens.textH1DeskLineHeight,
          letterSpacing: obTokens.textH1DeskKerning,
          [currentTheme.breakpoints.down('sm')]: {
            fontSize: obTokens.textH1MobSize,
            lineHeight: obTokens.textH1MobLineHeight,
            letterSpacing: obTokens.textH1MobKerning,
          },
        },
        textXs: {
          fontFamily: obTokens.textXsFamily,
          fontSize: obTokens.textXsSize,
          fontWeight: '400',
          lineHeight: obTokens.textXsLineHeight,
          letterSpacing: obTokens.textXsKerning,
        },
        textMdXs: {
          fontFamily: obTokens.textXsFamily,
          fontSize: obTokens.textXsSize,
          fontWeight: '700',
          lineHeight: obTokens.textXsLineHeight,
          letterSpacing: obTokens.textXsKerning,
        },
      };
      /**
       * Custom font for brandName for TAB enhancement
       */
      if (currentTheme.components?.MuiCssBaseline?.styleOverrides) {
        /**
         * the value of fontFamily needs to be the same as the value of the fontFamily in the tokens.
         */
        if (brandNameFromStore === BrandNameList.LC) {
          currentTheme.components.MuiCssBaseline.styleOverrides += `
              body {
                font-family: ${obTokens.textBaseFamily};
                font-size: ${obTokens.textBaseSize};
              }
              @font-face {
                font-family: 'sofia pro';
                src: url(${sofiaProRegular}) format('woff');
                font-weight: normal;
                font-style: normal;
              }
              @font-face {
                font-family: 'sofia pro';
                src: url(${sofiaProSemiBold}) format('woff');
                font-weight: 600;
                font-style: normal;
              }
              @font-face {
                font-family: 'sofia pro';
                src: url(${sofiaProBold}) format('woff');
                font-weight: 700;
                font-style: normal;
              }
              @font-face {
                font-family: 'minerva-modern';
                src: url(${minervaModernRegular}) format('woff');
                font-weight: 400;
                font-style: normal;
              }
              @font-face {
                font-family: 'minerva-modern';
                src: url(${minervaModernBold}) format('woff');
                font-weight: 700;
                font-style: normal;
              }
          `;
        } else if (brandNameFromStore === BrandNameList.TO) {
          currentTheme.components.MuiCssBaseline.styleOverrides += `
              body {
                font-family: ${obTokens.textBaseFamily};
                font-size: ${obTokens.textBaseSize};
              }
              @font-face {
                font-family: 'Helvetica for Target';
                src: url(${helveticaForTarget}) format('opentype');
                font-weight: normal;
                font-style: normal;
              }
              @font-face {
                font-family: 'Helvetica for Target';
                src: url(${helveticaForTargetBold}) format('woff');
                font-weight: 700;
                font-style: normal;
              }
          `;
        }
      }
    }

    return currentTheme;
  }, [brandNameFromStore, colorConfig]);

  const RouterComponent: React.ComponentType<any> =
    integrationMode === IntegrationModes.WCS ? HashRouter : BrowserRouter;

  return (
    <QueryClientProvider client={queryClient}>
      <LuxThemeProvider theme={theme}>
        <RouterComponent>
          <Environment
            environmentConfig={environmentConfig}
            brandName={brandName}
          >
            <UrlsProvider
              basePath={
                integrationMode === IntegrationModes.WCS ? '' : basePath
              }
            >
              <App
                mode={mode}
                hideHeader={hideHeader}
                collapsableMobileBookingDetails={
                  collapsableMobileBookingDetails
                }
              />
            </UrlsProvider>
          </Environment>
        </RouterComponent>
      </LuxThemeProvider>
    </QueryClientProvider>
  );
}

export default CommonConfiguration;
